<template>
  <Teleport to="#portal-target" :disabled="disablePortal">
    <transition :name="fade ? 'fade' : 'none'" mode="out-in">
      <div class="modalDialogWrapper" @mousedown.stop="handleMouseDown" @mouseup="handleMouseUp" v-if="show"
           ref="dialog" :class="[{withFooter: !!$slots['footer']}, {withIdent: !!app}]">
        <div :class="['modalDialog', {isWeb: !isMobile}]" :id="id ? id : false" :style="modalStyle">
          <AppIdent v-if="app" :userTenantId="userTenantId" :app="app" :tenants="tenants"
                    :showAppName="true" />
          <div v-if="$slots['heading'] || showCloseButton" class="mdlHeading"
               :class="[{withHeading: !!$slots['heading']}, headerClasses]">
            <button class="backButton" title="Back" @click="closeDialog" v-if="showBackButton">
              <BrandIcon class="icon" name="special/chevron-left"></BrandIcon>
            </button>
            <slot name="heading"></slot>
            <div class="endButtons">
              <slot name="extraButtons"></slot>
              <button class="endButton" title="Edit" @click="edit" v-if="showEditButton">
                <BrandIcon class="icon" name="light/edit-light"></BrandIcon>
              </button>
              <button class="endButton" title="Delete" @click="deleteEntry" v-if="showDeleteButton">
                <BrandIcon class="icon" name="light/delete-light"></BrandIcon>
              </button>
              <button class="endButton" title="Close" @click="closeDialog" v-if="showCloseButton">
                <BrandIcon class="icon closeIcon" name="special/dismiss"></BrandIcon>
              </button>
            </div>
          </div>
          <template v-if="scrollable">
            <ScrollableContent class="mdlContent scrollable"
                               :class="{ safariIos: isSafariIos, unpadded: unpaddedContent }"
                               ref="scrollableContent">
              <slot name="default"></slot>
            </ScrollableContent>
            <div class="progress" v-if="inProgress">
              <LoadingAnimation class="mdlContent loading"></LoadingAnimation>
            </div>
          </template>
          <template v-else>
            <div class="mdlContent" :class="{ unpadded: unpaddedContent }">
              <slot name="default"></slot>
            </div>
          </template>
          <div class="mdlFooter" v-if="$slots['footer'] || $slots['footer-left'] || $slots['footer-right']">
            <slot name="footer"></slot>
            <div class="leftAndRightContainer" v-if="$slots['footer-left'] || $slots['footer-right']">
              <div class="footer-left" v-if="$slots['footer-left']">
                <slot name="footer-left"></slot>
              </div>
              <div class="footer-right" v-if="$slots['footer-right']">
                <slot name="footer-right"></slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import {
  ComponentPublicInstance,
  computed,
  ComputedRef,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  Ref,
  ref,
} from 'vue';

import AppIdent from '@/components/AppIdent.vue';
import BrandIcon from '@/components/icons/BrandIcon.vue';
import LoadingAnimation from '@/components/LoadingAnimation.vue';
import ScrollableContent, { ScrollableContentApi } from '@/components/ScrollableContent.vue';
import BrowserDetection from '@/data/BrowserDetection';
import Tenant from '@/data/datatypes/Tenant';
import { trapFocus } from '@/data/helpers/DomHelper';
import { AppIdentDetails, MiniApp } from '@/data/tasks/MiniApp';
import { Routes } from '@/router';

// The duration in ms to wait before emitting the close event to allow the dialog to fade away
const fadeDuration = 500;

interface Props {
  id?: string;
  showCloseButton?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  showBackButton?: boolean;
  width?: string;
  height?: string;
  minHeight?: string;
  headerClasses?: string;
  scrollable?: boolean;
  outsideClickClosable?: boolean;
  fade?: boolean;
  inProgress?: boolean;
  disablePortal?: boolean;
  unpaddedContent?: boolean;
  // Props required when showing the app ident:
  app?: MiniApp | AppIdentDetails;
  userTenantId?: string;
  tenants?: Record<string, Tenant>;
  emitOnlyOnClose?: boolean; // If this is true, clicking close will only emit the close event, not close the dialog
}
const props = withDefaults(defineProps<Props>(), {
  showCloseButton: false,
  showEditButton: false,
  showDeleteButton: false,
  showBackButton: false,
  width: '40rem',
  height: '20rem',
  headerClasses: '',
  scrollable: true,
  outsideClickClosable: true,
  fade: true,
  inProgress: false,
  disablePortal: false,
  unpaddedContent: false,
  emitOnlyOnClose: false,
});

interface Emits {
  (e: 'open'): void,
  (e: 'close', event?: Event): void,
  (e: 'edit', event?: Event): void,
  (e: 'delete', event?: Event): void,
}
const emit = defineEmits<Emits>();

const dialog: Ref<HTMLElement | null> = ref(null);
const scrollableContent: Ref<ScrollableContentApi | null> = ref(null);

const mouseDownOnBackground: Ref<boolean> = ref(false);
const show: Ref<boolean> = ref(false);

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const isSafariIos: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isSafariIos() || BrowserDetection.isIosApp();
});

const modalStyle = computed(() => {
  return {
    width: props.width,
    height: props.height,
    minHeight: props.minHeight,
  };
});

function emitClose(event?: Event): Event | undefined {
  emit('close', event);
  return event;
}

function edit(event?: Event): Event | undefined {
  emit('edit', event);
  return event;
}

function deleteEntry(event?: Event): Event | undefined {
  emit('delete', event);
  return event;
}

function handleMouseDown(event: Event): void {
  // We handle mousedown and mouseup manually due to a Vue bug where doing a mousedown on the dialog itself
  // and then mouseup on the background (e.g. selecting some text in the dialog, where you happen to drag
  // the mouse off the edge of the dialog) would incorrectly trigger the background's click handler.
  if (dialog.value === event.target) {
    mouseDownOnBackground.value = true;
  }
}

function handleMouseUp(event: MouseEvent): void {
  if (props.outsideClickClosable && mouseDownOnBackground.value && dialog.value === event.target) {
    closeDialog(event);
  }
  mouseDownOnBackground.value = false;
}

function onkey(event: KeyboardEvent) {
  if (event.key === 'Escape' && Routes.isTopModal(closeDialog)) {
    event.stopPropagation();
    closeDialog(event);
  }

  if (event.key === 'Tab' && Routes.isTopModal(closeDialog)) {
    event.stopPropagation();
    trapFocus(dialog.value, event);
  }
}

function scrollToBottom(): void {
  if (scrollableContent.value) {
    scrollableContent.value?.scrollToBottom();
  }
}

function closeDialog(event?: Event): Event | undefined {
  if (props.emitOnlyOnClose) {
    emit('close');
    return;
  }
  show.value = false;
  setTimeout(() => {
    emitClose(event);
  }, props.fade ? fadeDuration : 0);
  return event;
}

function removeFromRouteModalCleanups(): void {
  Routes.popModal(closeDialog);
}

onBeforeMount(() => {
  Routes.pushModal(closeDialog);
});

onBeforeUnmount(() => {
  Routes.popModal(closeDialog);
  window.removeEventListener('keydown', onkey);
});

window.addEventListener('keydown', onkey);
nextTick(() => {
  show.value = true;
  setTimeout(() => {
    emit('open');
  }, props.fade ? fadeDuration : 0);
});

export interface ModalDialogApi extends ComponentPublicInstance {
  closeDialog: (event?: Event) => Event | undefined;
  scrollToBottom: () => void;
  removeFromRouteModalCleanups: () => void;
}

defineExpose({
  closeDialog,
  scrollToBottom,
  removeFromRouteModalCleanups,
});
</script>

<style scoped lang="scss">
  .modalDialogWrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba($colour-background-modal-body, 0.5);
    z-index: $z-layer-modaldialogs;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(0.25rem);
  }
  .modalDialog {
    background-color: $colour-background-light;
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    max-height: 100%;
    max-width: 100%;
    &.isWeb {
      max-width: calc(100vw - 5rem);
      max-height: calc(100vh - 5rem);
      border-radius: $size-border-radius-modal;
    }

    .mobile & {
      width: 100% !important;
      height: 100% !important;
      position: fixed;
      top: 0;
      left: 0;
      transform: inherit;
      max-width: inherit;
      max-height: inherit;
      overflow-x: auto;
    }

    .ident {
      height: 6rem;
      max-height: 6rem;
      min-width: 100%;
    }

    .mdlHeading {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 1rem 1rem 1rem 1.25rem;
      border-bottom: 1px solid $colour-border-light;
      font-weight: $weight-bold;
      .nativeIos & {
        padding-top: env(safe-area-inset-top);
      }

      &.withHeading {
        justify-content: space-between;
      }
      &.justifyCenter {
        justify-content: center;
      }
      .withIdent & {
        border: none;
      }

      .endButtons {
        display: flex;
      }
      .endButton {
        color: $colour-icon-primary;
        width: 1rem;
        height: 1rem;
        margin-left: 0.5rem;
        .icon {
          width: 1.5rem;
          height: 1.5rem;
          &.closeIcon {
            color: $colour-icon-primary-light;
          }
        }
      }
      .backButton {
        color: $colour-icon-primary;
        width: 3rem;
        height: 3rem;
        position: absolute;
        left: 0;
        .icon {
          width: 1rem;
          height: 1rem;
        }
      }
    }

    .mdlContent {
      flex: 1 1;
      padding: 1rem 1.25rem;
      min-height: 0;
      &.scrollable {
        padding-right: 0;
      }
      &.unpadded {
        padding: 0;
      }
      .nativeIos & {
        // the 'calc' seems superfluous here, but without it the SCSS doesn't compile.
        padding-bottom: calc(max(env(safe-area-inset-bottom), 1rem));
      }
      &.safariIos {
        padding-right: 1rem;
      }
      .withIdent & {
        padding-top: 0;
      }
      .withIdent.withFooter & {
        padding-bottom: 0;
      }
    }
    .mdlFooter {
      border-top: 1px solid $colour-border-light;
      padding: 1rem;
      .nativeIos & {
        // the 'calc' seems superfluous here, but without it the SCSS doesn't compile.
        padding-bottom: calc(max(env(safe-area-inset-bottom), 1rem));
      }
      .withIdent & {
        border: none;
      }
      .leftAndRightContainer {
        display: flex;
        .footer-left {
          display: flex;
          gap: 0.5rem;
          flex: 1;
          justify-content: flex-start;
        }
        .footer-right {
          display: flex;
          gap: 0.5rem;
          flex: 1;
          justify-content: flex-end;
        }
      }
    }
  }
  .backgroundMidGray {
    background-color: $colour-background-mid;
  }
  .fontRem {
    font-size: 1rem;
  }

  .progress {
    position: absolute;
    margin-top: 3rem;
    width: 100%;
    height: calc(100% - 3rem);
    background: rgba(white, 0.5);
    border-bottom-left-radius: $size-border-radius-modal;
    border-bottom-right-radius: $size-border-radius-modal;
  }

  .loading {
    position: relative;
    top: 50%;
    // -1.5rem to undo the margin that is enforced by the loading animation and the offset of the top bar
    transform: translate(0, calc(-50% - 3rem));
    height: 5rem;

    .animationContainer {
      :deep(.circle) {
        background-color: $colour-background-dark;
        &:last-child {
          background-color: $colour-background-dark;
        }
      }
    }
  }
</style>
